import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { AuthContextEnum } from '@feature-auth';
import {
  injectLandingActions,
  injectLandingSeo,
  WebAboutSection,
  WebCardV1,
  WebEntityHomeSection,
  WebLogo,
  WebParallaxBg,
  LANDING_VIDEOS,
  LandingScroller,
} from '@feature-landing';
import { MagicLinkConstants } from '@shared-app-config';
import { ButtonUIComponent, CheckmarkIconUIComponent, IconUIComponent } from '@shared-ui-kit';

const SERVICES_ID = 'services';
const SERVICES_UNFOLD_MS = 1000;

// The template puts a `<wbr>` before each segment so a long store URL wraps after its slashes, as
// in the design. Unlike a zero-width space, `<wbr>` stays out of copied text — the URL is filing
// evidence and has to paste as a working address.
// `(?!\/)` keeps the `//` of the scheme together, so the URL never wraps as `https:/` + `/…`.
const splitAfterSlashes = (url: string): string[] => url.split(/(?<=\/)(?!\/)/);

@Component({
  selector: 'fr8bot-customers-page',
  imports: [
    IconUIComponent,
    ButtonUIComponent,
    WebCardV1,
    WebAboutSection,
    WebParallaxBg,
    WebEntityHomeSection,
    NgTemplateOutlet,
    CheckmarkIconUIComponent,
    TranslatePipe,
    WebLogo,
  ],
  templateUrl: './customers-page.html',
  styleUrl: './customers-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomersPage {
  protected readonly videos = LANDING_VIDEOS;

  constructor() {
    injectLandingSeo('shippers');
    afterNextRender(() => this.booted.set(true));
  }

  protected readonly landing = injectLandingActions();
  protected readonly AUTH_CONTEXT = AuthContextEnum;
  protected readonly SERVICES_ID = SERVICES_ID;
  protected readonly appStoreUrl = MagicLinkConstants.shops.apple;
  protected readonly googlePlayUrl = MagicLinkConstants.shops.google;
  protected readonly appStoreUrlParts = splitAfterSlashes(this.appStoreUrl);
  protected readonly googlePlayUrlParts = splitAfterSlashes(this.googlePlayUrl);

  private readonly injector = inject(Injector);
  private readonly scroller = inject(LandingScroller);
  private readonly fragment = toSignal(inject(ActivatedRoute).fragment);
  private readonly servicesOpened = signal(false);
  // Hands the section's open state from the prerendered `:target` rule over to `servicesExpanded`:
  // the router changes the URL with `pushState`, which never updates `:target`.
  protected readonly booted = signal(false);

  // The `/shippers#services` deep link (cited in the trademark filing) must land on the open
  // section; the router's `anchorScrolling` (app.config) scrolls to it.
  protected readonly servicesExpanded = computed(
    () => this.servicesOpened() || this.fragment() === SERVICES_ID,
  );

  protected expandServices(): void {
    this.servicesOpened.set(true);
    // Glides for as long as the section unfolds (`.web-services--animated`), so its title arrives
    // under the header together with the content.
    afterNextRender(() => this.scroller.glideTo(SERVICES_ID, SERVICES_UNFOLD_MS), {
      injector: this.injector,
    });
  }
}
